import { matchPath } from "react-router";
import { Pagination } from "@sscs/types";
import {
  calculatePaginationOffset,
  capitalizeFirstLetter,
  convertSortToQueryString,
} from "@sscs/utils";
import {
  CATALOG_ROUTE,
  ORGANIZATION_CATALOG_ID,
  PACKAGE_ROUTE,
  SHAREABLE_REPORT_ROOT_PATH,
  SOFTWARE_CATALOG_ARCHITECTURE_PARAM,
  SOFTWARE_CATALOG_EXTENSION_PARAM,
  SOFTWARE_CATALOG_NO_EXTENSION,
  SOFTWARE_CATALOG_PLATFORM_ROUTE,
  SOFTWARE_CATALOG_ROUTE,
} from "Constants";
import { CatalogRoute, LocationState, OidcSsoLoginInfo } from "Types";
import { getRouteBasename } from ".";

export type PrimitiveObject = Record<
  string,
  string | number | boolean | null | undefined
>;

type ComplexObject = {
  pagination?: Pagination;
  sort?: {
    property: string;
    reverse: boolean;
  };
  filter?: PrimitiveObject;
};

type QueryParameters = PrimitiveObject | ComplexObject;

function isPrimitiveObject(
  parameters: Record<string, unknown>,
): parameters is PrimitiveObject {
  let result = true;

  Object.values(parameters)
    .filter((item) => item !== null && item !== undefined)
    .forEach((item) => {
      if (typeof item === "object") result = false;
    });

  return result;
}

export const encodeObjectToQueryString = (
  objectToEncode: PrimitiveObject,
): string => {
  return Object.keys(objectToEncode)
    .map((key) => {
      const value = objectToEncode[key];
      if (value !== null && value !== undefined)
        return key + "=" + encodeURIComponent(value);
    })
    .join("&");
};

const KEEP_EMPTY_PARAMS = [SOFTWARE_CATALOG_EXTENSION_PARAM];

type OidcAuthorizationRequest = {
  authorizationUrl: string;
  clientId: string;
  responseType: OidcSsoLoginInfo["oidc_response_type"];
  scopes: string;
  redirectUri: string;
  state: string;
  nonce: string;
};

export const buildOidcAuthorizationUrl = ({
  authorizationUrl,
  clientId,
  responseType,
  scopes,
  redirectUri,
  state,
  nonce,
}: OidcAuthorizationRequest): string => {
  const separator = authorizationUrl.includes("?") ? "&" : "?";

  return `${authorizationUrl}${separator}${encodeObjectToQueryString({
    client_id: clientId,
    response_type: responseType,
    scope: scopes,
    state,
    nonce,
    redirect_uri: redirectUri,
  })}`;
};

const OIDC_STATE_SEPARATOR = ";";

type OidcStateParam = {
  state: string;
  payload: string;
};

type ParsedOidcStateParam = {
  [Key in keyof OidcStateParam]: OidcStateParam[Key] | null;
};

export const buildOidcStateParam = ({
  state,
  payload,
}: OidcStateParam): string => `${state}${OIDC_STATE_SEPARATOR}${payload}`;

export const parseOidcStateParam = (
  stateParam: string | null,
): ParsedOidcStateParam => {
  if (!stateParam) return { state: null, payload: null };

  const separatorIndex = stateParam.indexOf(OIDC_STATE_SEPARATOR);

  if (separatorIndex === -1) return { state: stateParam, payload: null };

  const state = stateParam.slice(0, separatorIndex);
  const payload = stateParam.slice(separatorIndex + 1);

  return {
    state: state === "" ? null : state,
    payload: payload === "" ? null : payload,
  };
};

const primitiveObjectToQueryString = (params: PrimitiveObject): string => {
  return Object.keys(params)
    .filter(
      (key) =>
        params[key] !== null &&
        params[key] !== undefined &&
        (params[key] !== "" || KEEP_EMPTY_PARAMS.includes(key)) &&
        params[key] !== "ALL" &&
        !key.startsWith("ui_"),
    )
    .map((key) => {
      const value = params[key];

      if (typeof value === "boolean")
        return key + "=" + capitalizeFirstLetter(value.toString());
      else if (value !== null && value !== undefined)
        return key + "=" + encodeURIComponent(value);
    })
    .join("&");
};

export const paramSerializer = (params: QueryParameters): string => {
  if (isPrimitiveObject(params)) {
    return primitiveObjectToQueryString(params);
  } else {
    return Object.keys(params)
      .map((key) => {
        if (key === "sort" && params.sort) {
          return `sort_by=${convertSortToQueryString(params.sort)}`;
        } else if (key === "pagination" && params.pagination) {
          return `limit=${
            params.pagination?.itemsPerPage
          }&offset=${calculatePaginationOffset(params.pagination)}`;
        } else {
          const temp = params[key as keyof ComplexObject];

          if (typeof temp === "object")
            return temp !== undefined ? primitiveObjectToQueryString(temp) : "";
          else
            return (
              key + "=" + encodeURIComponent(temp !== undefined ? temp : "")
            );
        }
      })
      .join("&");
  }
};

function stateIsLocationState(state: unknown): state is LocationState {
  return (
    (state as LocationState).from !== undefined &&
    (state as LocationState).from.search !== undefined &&
    (state as LocationState).from.pathname !== undefined
  );
}

// This assists in re-routing users after they have passed authentication
// For users with no previous route to return to, this will pass them to the defatult '/' route
// For users that were on a page, have had authentication expire and then have to login again,
// this will reroute them back to the page they were on before.
// The component that takes care of storing the 'from' location is 'frontend/src/features/auth/components/RequireAuth.tsx'
export function maybeNavigateLocation(locationState?: unknown): string {
  if (!locationState) return "/";

  if (!stateIsLocationState(locationState)) return "/";

  const from = locationState.from.pathname || "/";
  const searchParams = locationState.from.search;

  return `${from}${searchParams ?? ""}`;
}

export const getRouterCompatiblePathname = (): string => {
  // react router strips basename from pathname
  const routeBasename = getRouteBasename();
  let pathname = window.location.pathname;

  if (
    routeBasename &&
    routeBasename !== "/" &&
    pathname.startsWith(routeBasename)
  ) {
    pathname = pathname.slice(routeBasename.length) || "/";
  }

  return pathname;
};

export const isShareableReportRoute = (): boolean => {
  return (
    matchPath(
      { path: SHAREABLE_REPORT_ROOT_PATH, end: false },
      getRouterCompatiblePathname(),
    ) !== null
  );
};

export const getCatalogRoute = (
  groupId: string,
  {
    catalogId = ORGANIZATION_CATALOG_ID,
    platform,
    architecture,
    extension,
    packageId,
  }: CatalogRoute,
): string => {
  let route = `/${SOFTWARE_CATALOG_ROUTE}/group/${groupId}/${CATALOG_ROUTE}/${catalogId}`;

  if (platform) {
    route += `/${SOFTWARE_CATALOG_PLATFORM_ROUTE}/${platform}`;
  }

  if (packageId) {
    route += `/${PACKAGE_ROUTE}/${packageId}`;
  }

  const params = new URLSearchParams();

  if (architecture) {
    params.set(SOFTWARE_CATALOG_ARCHITECTURE_PARAM, architecture);
  }

  if (extension !== undefined) {
    params.set(
      SOFTWARE_CATALOG_EXTENSION_PARAM,
      extension === "" ? SOFTWARE_CATALOG_NO_EXTENSION : extension,
    );
  }

  return params.size > 0 ? `${route}?${params}` : route;
};
